import Navbar from "../../components/Layout/Navbar";
import Footer from "../../components/Layout/Footer";
import PageMeta from "../../components/pagesHelmet/PageMeta";
import ANALYTICS_IMG from "./image/u2lr.jpeg";

const BRAND = {
  warning: "#3B82F6",
  warningDark: "#2563EB",
  warningSoft: "rgba(59, 130, 246, 0.08)",
  muted: "#6B7280",
  text: "#1F2937",
};

export default function AnalyticsPages() {
  return (
    <>
      <PageMeta
        title="Real-Time Analytics | U2LR"
        description="Track every click, understand traffic sources, and optimize your links with powerful real-time analytics."
      />
      <Navbar />

      <div style={{ color: BRAND.text }}>

        {/* ================= HERO ================= */}
        <section
          style={{
            background: `linear-gradient(135deg, #0f172a, ${BRAND.warningDark})`,
            color: "#fff",
          }}
        >
          <div className="container py-5">
            <div className="row py-5 align-items-center">
              <div className="col-lg-6">
                <h1 className="display-4 fw-bold mb-4">
                  Turn Click Data Into Smart Decisions
                </h1>

                <p className="lead mb-4" style={{ opacity: 0.9 }}>
                  Monitor performance in real time, understand your audience,
                  and optimize campaigns with precision analytics.
                </p>

                <div className="d-flex gap-3 flex-wrap">
                  <a
                    href="/register"
                    className="btn btn-light btn-lg rounded-pill px-4 fw-semibold"
                    style={{ color: BRAND.warningDark }}
                  >
                    Start Tracking Free
                  </a>

                  <a
                    href="/pricing"
                    className="btn btn-outline-light btn-lg rounded-pill px-4"
                  >
                    View Pricing
                  </a>
                </div>

                <p className="mt-3 small" style={{ opacity: 0.7 }}>
                  Real-Time • Accurate • Actionable Insights
                </p>
              </div>

              <div className="col-lg-6 text-center">
                <img
                  src={ANALYTICS_IMG}
                  alt="U2LR Analytics Dashboard"
                  className="img-fluid rounded-4 shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ================= TRUST STRIP ================= */}
        <section className="py-4 bg-light text-center">
          <div className="container">
            <div className="row text-muted small fw-semibold">
              <div className="col-md-3">✔ Real-time click tracking</div>
              <div className="col-md-3">✔ Geographic insights</div>
              <div className="col-md-3">✔ Device & browser data</div>
              <div className="col-md-3">✔ Campaign attribution</div>
            </div>
          </div>
        </section>

        {/* ================= FEATURES ================= */}
        <section className="py-5" style={{ background: BRAND.warningSoft }}>
          <div className="container py-4">

            <div className="text-center mb-5">
              <h2 className="fw-bold">Comprehensive Link Analytics</h2>
              <p style={{ color: BRAND.muted }}>
                Everything you need to measure, analyze, and optimize performance.
              </p>
            </div>

            <div className="row g-4">
              {[
                {
                  title: "Total & Unique Clicks",
                  desc: "Understand overall traffic and distinguish between repeat visitors and unique users.",
                },
                {
                  title: "Device & Browser Tracking",
                  desc: "See which devices and browsers your audience uses most.",
                },
                {
                  title: "Geographic Insights",
                  desc: "Identify top-performing countries and regions.",
                },
                {
                  title: "Referrer Tracking",
                  desc: "Discover where your traffic originates — social, ads, email, or direct.",
                },
                {
                  title: "Time-Based Performance",
                  desc: "Analyze click trends by hour, day, or campaign period.",
                },
                {
                  title: "Campaign Attribution",
                  desc: "Measure marketing ROI with accurate performance data.",
                },
              ].map((item, i) => (
                <div key={i} className="col-md-6 col-lg-4">
                  <div className="card h-100 border-0 shadow-sm rounded-4 p-4">
                    <h5 className="fw-bold mb-3">{item.title}</h5>
                    <p className="mb-0" style={{ color: BRAND.muted }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= BUSINESS IMPACT ================= */}
        <section className="py-5">
          <div className="container text-center">
            <h2 className="fw-bold mb-5">Turn Data Into Growth</h2>

            <div className="row g-4">
              {[
                {
                  title: "Optimize Campaigns",
                  desc: "Identify what works and scale your highest-performing channels.",
                },
                {
                  title: "Improve Conversions",
                  desc: "Adjust messaging and targeting using real engagement insights.",
                },
                {
                  title: "Maximize ROI",
                  desc: "Allocate budget based on measurable performance data.",
                },
              ].map((item, i) => (
                <div key={i} className="col-md-4">
                  <div className="shadow-sm rounded-4 p-4 h-100 bg-light">
                    <h6 className="fw-bold mb-2">{item.title}</h6>
                    <p className="mb-0" style={{ color: BRAND.muted }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= VALUE STRIP ================= */}
        <section className="py-5 text-center bg-light">
          <div className="container">
            <div className="row g-4">
              <div className="col-md-4">
                <h3 className="fw-bold">Real-Time</h3>
                <p style={{ color: BRAND.muted }}>
                  Instant visibility into performance.
                </p>
              </div>
              <div className="col-md-4">
                <h3 className="fw-bold">Accurate</h3>
                <p style={{ color: BRAND.muted }}>
                  Reliable, precise traffic insights.
                </p>
              </div>
              <div className="col-md-4">
                <h3 className="fw-bold">Actionable</h3>
                <p style={{ color: BRAND.muted }}>
                  Data you can immediately use to improve results.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================= FINAL CTA ================= */}
        <section
          className="py-5 text-center"
          style={{
            background: BRAND.warningDark,
            color: "#fff",
          }}
        >
          <div className="container py-4">
            <h2 className="fw-bold mb-3">
              Start Tracking Smarter Today
            </h2>
            <p className="mb-4" style={{ opacity: 0.85 }}>
              Gain the clarity you need to grow with confidence.
            </p>
            <a
              href="/register"
              className="btn btn-light btn-lg rounded-pill px-5 fw-semibold"
              style={{ color: BRAND.warningDark }}
            >
              Create Free Account
            </a>
          </div>
        </section>

      </div>

      <Footer />
    </>
  );
}
